import { syncAuthTokenToExternalExtension } from 'src/data/infinity';
import { isApplicationModeInfinity } from 'src/data/system';
import { getQueryParamSingleValue } from 'src/lib/browser';
import * as storage from 'src/lib/storage';

const TOKEN_FIELD = 'postMaloneAccessToken';

const QUERY_PARAMS = {
  authToken: 'auth_token',
} as const;

type TokenChangeListener = (oldValue: string | null, newValue: string | null) => void;
type UnsubscribeFunction = () => void;

let localAuthToken: string | undefined;

interface AuthenticationInterface {
  signIn(token: string): void;
  signOut(): void;
  getQueryToken(): string | undefined;
  isAuthenticated(): boolean;
  getToken(): string | undefined;
  notifyExtension(): void;
  subscribeToTokenChanges(listener: TokenChangeListener | null): UnsubscribeFunction | null;
}

export const Authentication: AuthenticationInterface = {
  /**
   * Save TOKEN in browser storage, or in memory if we are in Infinity and don't want to share "auth" state with other tabs.
   *
   * @param {string} token - authentication token
   */
  signIn(token: string): void {
    if (isApplicationModeInfinity()) {
      localAuthToken = token;
    } else {
      storage.writeItem(TOKEN_FIELD, token);
      this.notifyExtension();
    }
  },

  /**
   * Remove TOKEN from browser storage, or from memory if we were logged in Infinity.
   */
  signOut(): void {
    if (isApplicationModeInfinity()) {
      localAuthToken = undefined;
    } else {
      storage.removeItem(TOKEN_FIELD);
      this.notifyExtension();
    }
  },

  getQueryToken(): string | undefined {
    return getQueryParamSingleValue(QUERY_PARAMS.authToken);
  },

  isAuthenticated(): boolean {
    const token = this.getToken();
    return Boolean(token);
  },

  getToken(): string | undefined {
    // Token from memory is the only token that should be used in Infinity
    if (isApplicationModeInfinity()) {
      return localAuthToken;
    }

    // Token from query params has the second highest priority
    const queryToken = this.getQueryToken();
    if (queryToken) {
      return queryToken;
    }

    // Token from storage has the lowest priority
    return storage.readItem(TOKEN_FIELD);
  },

  /**
   * Notify extension about authentication status change
   * Do not react to errors
   */
  notifyExtension(): void {
    // "embedded" Platform should not send out "external" messages
    if (isApplicationModeInfinity()) {
      return;
    }

    if (this.isAuthenticated()) {
      const token = this.getToken();
      syncAuthTokenToExternalExtension(token).catch(() => {
        /* do nothing */
      });
    } else {
      syncAuthTokenToExternalExtension(undefined).catch(() => {
        /* do nothing */
      });
    }
  },

  subscribeToTokenChanges(listener: TokenChangeListener | null): UnsubscribeFunction | null {
    if (!listener) {
      return null;
    }

    const handler = (event: StorageEvent): void => {
      // HINT: this fixes issue in Safari - we don't react to event, when it was fired for the same tab which changed value
      if (document.hasFocus()) {
        return;
      }

      if (event.key === TOKEN_FIELD) {
        listener(event.oldValue, event.newValue);
      }
    };
    window.addEventListener('storage', handler);
    return (): void => window.removeEventListener('storage', handler);
  },
};
